import { useCuttingStore } from './state/useCuttingStore';
import { CutMapCanvas } from './CutMap';
import { DEFAULT_MIN_OFFCUT_MM } from './lib/resultMetrics';
import { buildOffcutList } from './lib/offcutList';

/**
 * Block 14 «Раскрой» — печать ВСЕХ листов раскроя (PR block-14-print-all-sheets).
 *
 * Print-only (`.cut-print-only`: `display:none` на экране, `@media print` → показ).
 * Экранный `CutMap` — пейджер ◀▶, один лист за раз — НЕ меняется; в печати он
 * скрыт правилом `.result-canvas-wrap:not(.cut-print-sheet){display:none}`, а вместо
 * него печатается каждый лист на СВОЕЙ странице (стр.1 = лист 1, …, стр.N = лист N).
 * Финальная страница (метрики + список деталей) — соседние `ResultsTab` +
 * `CutPrintDetails` (см. `CuttingProject`).
 *
 * Каждый лист — `.result-canvas-wrap.cut-print-only.cut-print-sheet` (прямой
 * flex-child `.cutting-print-region`): класс `result-canvas-wrap` наследует
 * print-правила `order:1 / break-after:page / break-inside:avoid` и
 * `height:160mm` для `.sc-svg-canvas` — каждый лист влезает на свою страницу.
 * Рендер полотна — общий `CutMapCanvas` (без drift с экранной картой), зум/пан
 * выключен (`enablePanZoom={false}`) — печатные клоны на экране скрыты.
 *
 * 5 состояний (P-021): как и `CutMap`, при `cutStatus !== 'success'` или отсутствии
 * листов возвращает `null` — stub отдаёт соседний `ResultsTab`.
 */
export function CutPrintMap() {
  const lastResult = useCuttingStore((s) => s.lastResult);
  const cutStatus = useCuttingStore((s) => s.cutStatus);
  const parts = useCuttingStore((s) => s.document.parts);
  const minOffcutSize = useCuttingStore((s) => s.document.settings.minOffcutSize);
  // Тогглы карты (UI-only, общий стор): печать следует экранному выбору Кромки/Пазы.
  const showEdges = useCuttingStore((s) => s.mapShowEdges);
  const showGrooves = useCuttingStore((s) => s.mapShowGrooves);

  if (cutStatus !== 'success' || !lastResult || lastResult.sheets.length === 0) return null;
  const sheets = lastResult.sheets;

  const threshold = minOffcutSize ?? DEFAULT_MIN_OFFCUT_MM;
  const total = sheets.length;
  // Сквозная нумерация остатков через все листы (совпадает с «Список остатков»).
  const { startBySheet } = buildOffcutList(lastResult, threshold);

  return (
    <>
      {sheets.map((sheet, idx) => {
        // Габарит листа: целый — размер листа; лист-остаток — размер самого остатка
        // (тот же расчёт, что в экранном CutMap — единый текст заголовка).
        const sheetDims = `${Math.round(sheet.sheetLength)} × ${Math.round(sheet.sheetWidth)}`;
        const titleSuffix = sheet.isOffcut ? ` · Остаток ${sheetDims}` : ` — ${sheetDims}`;

        return (
          <div
            key={idx}
            className="result-canvas-wrap cut-print-only cut-print-sheet"
            data-testid="cut-print-sheet"
          >
            <div className="result-canvas-title">
              📐 Карта раскроя — лист {idx + 1} из {total}
              {titleSuffix}
            </div>
            <CutMapCanvas
              sheet={sheet}
              parts={parts}
              threshold={threshold}
              idx={idx}
              total={total}
              titleSuffix={titleSuffix}
              offcutStart={startBySheet[idx] ?? 0}
              enablePanZoom={false}
              showEdges={showEdges}
              showGrooves={showGrooves}
            />
          </div>
        );
      })}
    </>
  );
}
